import { Fragment, type ReactNode } from "react";

export type DocMeta = {
  projectTitle?: string | null;
  clientName?: string | null;
  freelancerName?: string | null;
  projectType?: string | null;
  totalPrice?: number | string | null;
  startDate?: string | null;
  endDate?: string | null;
};

function isHeading(line: string) {
  if (line.length > 72) return false;
  if (!/[A-Za-z]/.test(line)) return false;
  // All-caps section header (e.g. "SCOPE OF WORK") or numbered legal clause
  // (e.g. "1. PROJECT SCOPE AND DELIVERABLES").
  return line === line.toUpperCase();
}

function isBullet(line: string) {
  return /^[-*•–]\s+/.test(line);
}

// Renders raw AI text into clean, semantic blocks: headings, bullet lists,
// horizontal rules, and paragraphs (one per source line).
function FormattedText({ text }: { text: string }) {
  const lines = text.replace(/\r/g, "").split("\n");
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];
  let key = 0;

  const flushBullets = () => {
    if (!bullets.length) return;
    const items = bullets;
    blocks.push(
      <ul key={`u${key++}`} className="pd-ul">
        {items.map((b, i) => (
          <li key={i}>{b}</li>
        ))}
      </ul>,
    );
    bullets = [];
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushBullets();
      continue;
    }
    if (isBullet(line)) {
      bullets.push(line.replace(/^[-*•–]\s+/, ""));
      continue;
    }
    flushBullets();
    if (/^[-_=]{3,}$/.test(line)) {
      blocks.push(<hr key={`h${key++}`} className="pd-hr" />);
      continue;
    }
    if (isHeading(line)) {
      blocks.push(
        <h2 key={`t${key++}`} className="pd-h2">
          {line}
        </h2>,
      );
      continue;
    }
    blocks.push(
      <p key={`p${key++}`} className="pd-p">
        {line}
      </p>,
    );
  }
  flushBullets();

  return <>{blocks}</>;
}

function formatDate(value?: string | null) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function Letterhead({ label, meta }: { label: string; meta: DocMeta }) {
  const parties =
    meta.freelancerName && meta.clientName
      ? `Prepared by ${meta.freelancerName} for ${meta.clientName}`
      : meta.clientName
        ? `Prepared for ${meta.clientName}`
        : null;
  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <header className="pd-header">
      <div className="pd-brand">DealDraft</div>
      <h1 className="pd-title">{label}</h1>
      {meta.projectTitle ? <div className="pd-subtitle">{meta.projectTitle}</div> : null}
      <div className="pd-meta">
        {parties ? <span>{parties}</span> : null}
        <span>{today}</span>
      </div>
    </header>
  );
}

// Print-only, professionally formatted proposal + contract.
// Hidden on screen (display:none) and revealed by the @media print rules.
export function PrintableDocument({
  proposal,
  contract,
  meta,
}: {
  proposal: string;
  contract: string;
  meta: DocMeta;
}) {
  const sections: { label: string; text: string }[] = [];
  if (proposal?.trim()) sections.push({ label: "Client Proposal", text: proposal });
  if (contract?.trim()) sections.push({ label: "Service Contract", text: contract });

  return (
    <div className="hidden print:block print-root">
      {sections.map((s, i) => (
        <Fragment key={s.label}>
          {i > 0 ? <div className="page-break" /> : null}
          <article className="print-document">
            <Letterhead label={s.label} meta={meta} />
            <FormattedText text={s.text} />
          </article>
        </Fragment>
      ))}
    </div>
  );
}
